import React from "react";
import * as Icon from "react-feather";

import "./statement-reading-panel.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsTieStrip from "../kit/ToolsTieStrip";
import ToolsModal from "../kit/ToolsModal";

class StatementReadingPanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			lines: [],
			tie: null,
			statement: null,
			editingLineId: null,
			draft: { date: "", description: "", amount: "" },
			saving: false,
			error: "",
			booksToAgreeTo: null,
			pushing: false,
			addingALine: false,
			newLine: { date: "", description: "", amount: "" },
		};
	}

	theStatementMovesMoreThanTheBooks = () => {
		const { shortfall } = this.props;
		const movesMore = !!(shortfall && shortfall.shortBy > 0);
		return movesMore;
	};

	theBooksMoveMoreThanTheStatement = () => {
		const { shortfall } = this.props;
		const movesMore = !!(shortfall && shortfall.shortBy < 0);
		return movesMore;
	};

	theReadingDoesNotReachTheClosingBalance = () => {
		const { tie, statement } = this.state;
		const itIsACard = !!(statement && statement.statementType === "credit_card");
		const aDifferenceIsKnown = !!(tie && !tie.ok && tie.delta !== null && tie.delta !== undefined);
		const theDifferenceIsRealMoney = aDifferenceIsKnown && Math.abs(Number(tie.delta)) > 0.009;
		const isShort = theDifferenceIsRealMoney && !itIsACard;
		return isShort;
	};

	readWhatTheReadingIsShortBy = () => {
		const { tie } = this.state;
		if (!this.theReadingDoesNotReachTheClosingBalance()) return "";

		const shortBy = Math.round(-Number(tie.delta) * 100) / 100;
		const asTypedIntoTheAmountBox = String(shortBy);
		return asTypedIntoTheAmountBox;
	};

	onStartAddingALine = () => {
		const { shortfall } = this.props;
		const { statement } = this.state;

		const amountTheStatementIsShortBy = this.theStatementMovesMoreThanTheBooks() ? String(shortfall.shortBy) : "";
		const amountTheReadingIsShortBy = this.readWhatTheReadingIsShortBy();
		const amountToOffer = amountTheStatementIsShortBy || amountTheReadingIsShortBy;
		const lastDayOfTheStatement = (shortfall && shortfall.periodEnd) || (statement && statement.periodEnd) || "";

		this.update({
			addingALine: true,
			editingLineId: null,
			error: "",
			newLine: {
				date: String(lastDayOfTheStatement || "").slice(0, 10),
				description: "",
				amount: amountToOffer,
			},
		});
	};

	onStopAddingALine = () => this.update({ addingALine: false, error: "" });

	onNewLineChanged = ({ field, value }) => this.update({ newLine: { ...this.state.newLine, [field]: value } });

	onSaveNewLine = async () => {
		const { statementId, wpfEngagementId, onReadingChanged } = this.props;
		const { newLine } = this.state;

		await this.update({ saving: true, error: "" });
		const result = await BookkeepingService.recordALineTheReaderMissed({
			statementId,
			wpfEngagementId,
			date: newLine.date,
			description: newLine.description,
			amount: newLine.amount,
		});

		if (!result || !result.added) {
			await this.update({ saving: false, error: (result && result.reason) || "That line could not be added." });
			return;
		}

		const itDidNotReachTheBooks = !result.reachedTheBooks;
		await this.update({
			saving: false,
			addingALine: false,
			error: itDidNotReachTheBooks ? "The statement now reads correctly, but the line did not reach the books: " + (result.reason || "no reason given") : "",
		});
		await this.loadTheReading();
		if (onReadingChanged) onReadingChanged();
	};

	async componentDidMount() {
		await this.loadTheReading();
	}

	loadTheReading = async () => {
		const reading = await BookkeepingService.readStatementReading({ statementId: this.props.statementId });

		if (reading.error) {
			await this.update({ loading: false, error: reading.error });
			return;
		}

		await this.update({ loading: false, lines: reading.lines || [], tie: reading.tie || null, statement: reading.statement || null, error: "" });
	};

	onStartCorrecting = ({ line }) => {
		this.update({
			editingLineId: line.id,
			draft: { date: String(line.date || "").slice(0, 10), description: line.description || "", amount: String(line.amount) },
			error: "",
		});
	};

	onStopCorrecting = () => this.update({ editingLineId: null, error: "" });

	onDraftChanged = ({ field, value }) => this.update({ draft: { ...this.state.draft, [field]: value } });

	onSaveCorrection = async () => {
		const { statementId, onReadingChanged } = this.props;
		const { editingLineId, draft } = this.state;

		await this.update({ saving: true, error: "" });

		const saved = await BookkeepingService.correctStatementReading({
			statementId,
			lineId: editingLineId,
			date: draft.date,
			description: draft.description,
			amount: draft.amount === "" ? null : parseFloat(draft.amount),
		});

		if (saved.error || saved.corrected === false) {
			await this.update({ saving: false, error: saved.error || saved.reason });
			return;
		}

		await this.update({
			saving: false,
			editingLineId: null,
			lines: saved.lines || [],
			tie: saved.tie || null,
			booksToAgreeTo: saved.booksWouldNeed && saved.booksWouldNeed.transactionId ? { ...saved.booksWouldNeed, lineId: editingLineId } : null,
		});
		if (onReadingChanged) onReadingChanged();
	};

	onLeaveTheBooksAlone = () => this.update({ booksToAgreeTo: null });

	onPutItInTheBooks = async () => {
		const { booksToAgreeTo } = this.state;

		await this.update({ pushing: true });
		const pushed = await BookkeepingService.putStatementCorrectionInTheBooks({
			statementId: this.props.statementId,
			lineId: booksToAgreeTo.lineId,
			transactionId: booksToAgreeTo.transactionId,
		});
		await this.update({ pushing: false });

		if (pushed.error || pushed.pushed === false) {
			await this.update({ booksToAgreeTo: null, error: pushed.error || pushed.reason });
			return;
		}

		await this.update({ booksToAgreeTo: null });
		if (this.props.onReadingChanged) this.props.onReadingChanged();
	};

	renderBooksConfirmation = () => {
		const { booksToAgreeTo, pushing } = this.state;
		if (!booksToAgreeTo) return null;

		const money = value => UtilityService.formatSignedCurrency({ value });
		const { from, to, classification } = booksToAgreeTo;
		const amountChanged = Number(from.amount) !== Number(to.amount);

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" disabled={pushing} onClick={this.onLeaveTheBooksAlone}>
						Leave the books alone
					</button>
					<button type="button" className="tk-button tk-button--primary" disabled={pushing} onClick={this.onPutItInTheBooks}>
						<Icon.Check size={16} />
						{pushing ? "Updating…" : "Update the books"}
					</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={this.onLeaveTheBooksAlone} title="This line is already in the books" width={620} footer={footer}>
				<p className="srp__confirm-line">
					The statement now reads differently from the transaction booked off it. Updating changes the figure the trial balance and the financial
					statements are built from.
				</p>
				<div className="srp__confirm-change">
					<div className="srp__confirm-side">
						<span className="tk-section-label">In the books now</span>
						<span className="srp__confirm-amount">{money(from.amount)}</span>
						<span className="srp__confirm-meta">{from.date}</span>
						<span className="srp__confirm-meta">{from.description || "(nothing written)"}</span>
					</div>
					<Icon.ArrowRight size={16} className="srp__confirm-arrow" />
					<div className="srp__confirm-side srp__confirm-side--to">
						<span className="tk-section-label">The statement says</span>
						<span className="srp__confirm-amount">{money(to.amount)}</span>
						<span className="srp__confirm-meta">{to.date}</span>
						<span className="srp__confirm-meta">{to.description || "(nothing written)"}</span>
					</div>
				</div>
				{amountChanged && (
					<p className="srp__confirm-note">
						This moves {money(Math.abs(Number(to.amount) - Number(from.amount)))}
						{classification ? ` in and out of ${classification}` : ""}. What the row held before is written onto it, so the change can be traced.
					</p>
				)}
				<p className="srp__confirm-note">
					Leaving the books alone keeps the correction on the statement only. The difference then shows as a statement that does not reconcile,
					which is where it belongs until somebody decides.
				</p>
			</ToolsModal>
		);
	};

	renderTie = () => {
		const { tie, statement, lines } = this.state;
		if (!tie || !statement) return null;

		const reached = tie.computed;
		if (reached === null || reached === undefined) return null;

		const movement = reached - Number(statement.openingBalance || 0);
		const ties = !!tie.ok;
		const shortBy = UtilityService.formatSignedCurrency({ value: Math.abs(tie.delta) });
		const printed = UtilityService.formatSignedCurrency({ value: statement.closingBalance });

		return (
			<ToolsTieStrip
				ties={ties}
				opening={statement.openingBalance}
				lineCount={lines.length}
				movement={movement}
				reached={reached}
				note={statement.statementType === "credit_card" ? "as the card counts them" : null}
				verdict={ties ? "reaches the closing balance printed on the statement" : `${shortBy} away from the ${printed} printed on the statement`}
			/>
		);
	};

	renderCorrectionForm = () => {
		const { draft, saving } = this.state;

		return (
			<div className="srp__row srp__row--editing">
				<input type="date" className="tk-input srp__cell-input" value={draft.date} onChange={event => this.onDraftChanged({ field: "date", value: event.target.value })} />
				<input
					type="text"
					className="tk-input srp__cell-input"
					value={draft.description}
					onChange={event => this.onDraftChanged({ field: "description", value: event.target.value })}
				/>
				<input
					type="number"
					step="0.01"
					className="tk-input srp__cell-input srp__amount"
					value={draft.amount}
					onChange={event => this.onDraftChanged({ field: "amount", value: event.target.value })}
				/>
				<span className="srp__row-actions">
					<button type="button" className="tk-text-action" disabled={saving} onClick={this.onSaveCorrection}>
						{saving ? "Saving…" : "Save"}
					</button>
					<button type="button" className="tk-text-action tk-text-action--muted" disabled={saving} onClick={this.onStopCorrecting}>
						Cancel
					</button>
				</span>
			</div>
		);
	};

	renderNewLineForm = () => {
		const { newLine, saving } = this.state;
		const canSave = !saving && newLine.description.trim() !== "" && newLine.date !== "" && newLine.amount !== "";

		return (
			<div className="srp__row srp__row--editing srp__row--adding">
				<input type="date" className="tk-input srp__cell-input" value={newLine.date} onChange={event => this.onNewLineChanged({ field: "date", value: event.target.value })} />
				<input
					type="text"
					className="tk-input srp__cell-input"
					placeholder="The wording on the statement line"
					value={newLine.description}
					autoFocus
					onChange={event => this.onNewLineChanged({ field: "description", value: event.target.value })}
				/>
				<input
					type="number"
					step="0.01"
					className="tk-input srp__cell-input srp__amount"
					value={newLine.amount}
					onChange={event => this.onNewLineChanged({ field: "amount", value: event.target.value })}
				/>
				<span className="srp__row-actions">
					<button type="button" className="tk-text-action" disabled={!canSave} onClick={this.onSaveNewLine}>
						{saving ? "Adding…" : "Add line"}
					</button>
					<button type="button" className="tk-text-action tk-text-action--muted" disabled={saving} onClick={this.onStopAddingALine}>
						Cancel
					</button>
				</span>
			</div>
		);
	};

	renderAddALine = () => {
		const { isSignedOff, shortfall } = this.props;
		const { addingALine } = this.state;
		if (isSignedOff) return null;
		if (addingALine) return this.renderNewLineForm();

		const theStatementMovesMore = this.theStatementMovesMoreThanTheBooks();
		const theBooksMoveMore = this.theBooksMoveMoreThanTheStatement();
		const theReadingIsShort = this.theReadingDoesNotReachTheClosingBalance();
		const aLineWasMissed = theStatementMovesMore || theReadingIsShort;
		const booksHoldExtra = theBooksMoveMore ? UtilityService.formatSignedCurrency({ value: Math.abs(shortfall.shortBy) }) : "";

		return (
			<div className="srp__add">
				<button type="button" className="tk-text-action" onClick={this.onStartAddingALine}>
					<Icon.Plus size={14} />
					{aLineWasMissed ? "Add the line the reader missed" : "Add a line read off the statement"}
				</button>
				{theStatementMovesMore && <span className="srp__add-hint">The amount is filled in with what the statement is short by.</span>}
				{theReadingIsShort && !theStatementMovesMore && (
					<span className="srp__add-hint">
						The amount is filled in with what this reading is short of the closing balance the statement prints. It goes onto the
						statement's own record and into the books.
					</span>
				)}
				{theBooksMoveMore && (
					<span className="srp__add-hint">
						The books hold {booksHoldExtra} more than this statement shows. Find the booked line that is not on it rather than adding one here.
					</span>
				)}
			</div>
		);
	};

	renderLine = ({ line }) => {
		const { isSignedOff } = this.props;
		if (this.state.editingLineId === line.id) return <React.Fragment key={line.id}>{this.renderCorrectionForm()}</React.Fragment>;

		const readDifferently = line.wasCorrected && line.asFirstRead;

		return (
			<div key={line.id} className="srp__row">
				<span className="srp__date">{UtilityService.formatDate({ value: line.date, format: "MMM D, YYYY", fallback: "—" })}</span>
				<span className="srp__description">
					{line.description || <span className="tk-text-faint">(nothing written)</span>}
					{readDifferently && (
						<span className="srp__provenance" title={`Read as ${UtilityService.formatSignedCurrency({ value: line.asFirstRead.amount })} — corrected by hand`}>
							corrected from {UtilityService.formatSignedCurrency({ value: line.asFirstRead.amount })}
						</span>
					)}
					{line.wasAddedByHand && (
						<span className="srp__provenance" title="The reader never produced this line; a person recorded it from the statement">
							added by hand
						</span>
					)}
				</span>
				<span className={`srp__amount ${Number(line.amount) < 0 ? "srp__amount--out" : "srp__amount--in"}`}>{UtilityService.formatSignedCurrency({ value: line.amount })}</span>
				<span className="srp__row-actions">
					{!isSignedOff && (
						<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.onStartCorrecting({ line })}>
							Correct
						</button>
					)}
				</span>
			</div>
		);
	};

	render() {
		const { loading, lines, error } = this.state;

		if (loading) return <div className="srp__loading">Reading the statement…</div>;

		return (
			<div className="srp">
				<p className="srp__intro">
					What the reader took this statement to say. Correcting a figure changes this reading, not the document, and what it first read is kept
					beside it.
				</p>
				<div className="srp__table">
					<div className="srp__row srp__row--headings">
						<span>Date</span>
						<span>What it says on the statement</span>
						<span className="srp__amount">Amount</span>
						<span />
					</div>
					<div className="srp__rows">{lines.map(line => this.renderLine({ line }))}</div>
				</div>
				{this.renderAddALine()}
				{this.renderTie()}
				{this.renderBooksConfirmation()}
				{error && (
					<div className="srp__error">
						<Icon.AlertCircle size={13} />
						{error}
					</div>
				)}
			</div>
		);
	}
}

export default StatementReadingPanel;
